import type { OffsetPaginationMeta } from "@/types/pagination";

export enum AdAccountStatus {
  ACTIVE = "active",
  DISCONNECTED = "disconnected",
  TOKEN_EXPIRED = "token_expired",
}

export interface AdAccount {
  id: string;
  externalId: string;
  name: string;
  status: AdAccountStatus;
  lastSyncedAt: string | null;
  currency: string | null;
  accountStatus: number | null;
  tokenExpiresAt: string | null;
  organizationId: string;
  workspaceId: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface AdInsights {
  spend?: string;
  impressions?: string;
  clicks?: string;
  cpc?: string;
  cpm?: string;
  cpp?: string;
  currency?: string;
  cost_per_result?: string;
  cost_per_inline_link_click?: string;
  cost_per_unique_click?: string;
  cost_per_conversion?: string;
  cost_per_estimated_ad_recallers?: string;
  conversion_values?: Record<string, unknown>[];
  conversions?: Record<string, unknown>[];
  cost_per_action_type?: Record<string, unknown>[];
}

export interface AdWithInsights {
  id: string;
  externalId: string;
  name: string;
  description: string | null;
  aiGeneratedDescription: string | null;
  videoUrl: string | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  productId: string | null;
  source: string;
  mediaId: string | null;
  adAccountId: string | null;
  adsetId: string | null;
  campaignId: string | null;
  /** Meta ids/names of the ad's parents, resolved server-side. */
  adsetExternalId: string | null;
  adsetName: string | null;
  campaignExternalId: string | null;
  campaignName: string | null;
  status: string | null;
  previewUrl: string | null;
  instagramPermalinkUrl: string | null;
  adInsights: AdInsights | null;
  conversionType: "message" | "form" | "website" | "other" | null;
  organizationId: string;
  workspaceId: string;
  createdAt: string;
  updatedAt: string;
  attachedSummary: AdAttachmentSummary | null;
}

export interface MetaAdAccountOption {
  externalId: string;
  accountId: string;
  name: string;
  currency: string;
  accountStatus: number;
  isAlreadyConnected: boolean;
}

export interface ConnectResponse {
  token: string;
  expiresIn: number;
  accounts: MetaAdAccountOption[];
}

export interface RefreshAdResponse {
  updated: boolean;
  ad?: AdWithInsights;
  message?: string;
}

export type AdsQueryData = {
  result: AdWithInsights[];
  meta: OffsetPaginationMeta;
};

export interface SelectAdAccountsPayload {
  token: string;
  expiresIn: number;
  accounts: Array<{
    externalId: string;
    name: string;
    currency?: string;
    accountStatus?: number;
  }>;
}

export enum AdAttachmentType {
  PRODUCTS = "products",
  TREATMENTS = "treatments",
}

export interface AdAttachmentItem {
  id: string;
  name: string;
  description: string | null;
}

export interface AdAttachmentsResponse {
  type: AdAttachmentType;
  items: AdAttachmentItem[];
}

export interface AdAttachmentSummary {
  type: AdAttachmentType;
  items: AdAttachmentItem[];
}

export interface UpsertAdAttachmentsPayload {
  itemIds: string[];
}

export interface UpdateAdAiGeneratedDescriptionPayload {
  aiGeneratedDescription: string;
}

export interface AdsFiltersState {
  search: string;
  statuses: string[];
  accountId: string | null;
  /** Meta campaign ids the list is narrowed to. */
  campaignIds: string[];
  /** Meta ad set ids the list is narrowed to. */
  adsetIds: string[];
}
